import React from 'react'

interface Step {
  id: number
  title: string
  description?: string
  isCompleted: boolean
  isActive: boolean
}

interface StepperProps {
  steps: Step[]
  currentStep: number
  totalSteps: number
}

const Stepper: React.FC<StepperProps> = ({ steps, currentStep, totalSteps }) => {
  const completedSteps = steps.filter(step => step.isCompleted).length
  const progressPercentage = (completedSteps / totalSteps) * 100

  return (
    <div className="stepper-container">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-bold fs-6 text-gray-700">
            {completedSteps}/{totalSteps} steps completed
          </span>
          <span className="text-gray-700 fs-7">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="progress" style={{ height: '8px' }}>
          <div
            className="progress-bar bg-primary"
            role="progressbar"
            style={{ width: `${progressPercentage}%` }}
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      <div className="steps-list">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`step-item d-flex align-items-center mb-3 ${
              step.isCompleted ? 'completed' : step.isActive ? 'active' : ''
            }`}
          >
            <div className="step-indicator me-3">
              {step.isCompleted ? (
                <div 
                  className="step-completed d-flex align-items-center justify-content-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#0e9f6e" className="icon icon-tabler icons-tabler-filled icon-tabler-circle-check">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" />
                  </svg>
                </div>
              ) : (
                <div 
                  className={`step-number d-flex align-items-center justify-content-center ${
                    step.isActive ? 'active' : ''
                  }`}
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    color: step.isActive ? '#0E9F6E' : '#6a7282',
                    fontWeight: '600',
                    fontSize: '10px',
                    transition: 'all 0.3s ease'
                  }}
                >
                 <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-circle-dashed"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8.56 3.69a9 9 0 0 0 -2.92 1.95" /><path d="M3.69 8.56a9 9 0 0 0 -.69 3.44" /><path d="M3.69 15.44a9 9 0 0 0 1.95 2.92" /><path d="M8.56 20.31a9 9 0 0 0 3.44 .69" /><path d="M15.44 20.31a9 9 0 0 0 2.92 -1.95" /><path d="M20.31 15.44a9 9 0 0 0 .69 -3.44" /><path d="M20.31 8.56a9 9 0 0 0 -1.95 -2.92" /><path d="M15.44 3.69a9 9 0 0 0 -3.44 -.69" /></svg>
                </div>
              )}
            </div>

            <div className="step-content">
              <div 
                className={`step-title fw-semibold ${
                  step.isCompleted ? 'text-success' : step.isActive ? 'text-primary' : 'text-gray-700'
                }`}
                style={{
                  textDecoration: step.isCompleted ? 'line-through' : 'none'
                }}
              >
                {step.title}
              </div>
              {step.description && (
                <div className="step-description text-gray-700 fs-7 mt-1">
                  {step.description}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <style>
        {`
          .stepper-container {
            padding: 20px;
          }
          
          .step-indicator {
            flex-shrink: 0;
          }
          
          .step-item {
            transition: all 0.3s ease;
          }
          
          .progress {
            border-radius: 10px;
            background-color: #e9ecef;
          }
          
          .progress-bar {
            border-radius: 10px;
            transition: width 0.6s ease;
          }
        `}
      </style>
    </div>
  )
}

export default Stepper
